import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../AuthProvider';
import './ViewTasks.css';
import GmailScreenshotUpload from '../components/GmailScreenshotUpload';
import ConfirmModal from '../components/ConfirmModal';

const getUrlQueryKeys = (url) => {
    try {
        return new Set([...new URL(url || '', window.location.origin).searchParams.keys()]);
    } catch {
        return new Set();
    }
};

const isGreenhouseTask = (task) => {
    const url = task?.jobUrl || '';
    const source = task?.source || '';
    const haystack = `${url} ${source}`.toLowerCase();
    const queryKeys = getUrlQueryKeys(url);
    return haystack.includes('greenhouse') || ['gh_jid', 'gh_src'].some((k) => queryKeys.has(k));
};

const isLeverTask = (task) => {
    const url = task?.jobUrl || '';
    const source = task?.source || '';
    return `${url} ${source}`.toLowerCase().includes('lever');
};

const matchesPlatformFilter = (task, platformFilter) => {
    if (platformFilter === 'all') return true;
    if (!task?.has_autofill_data) return false;
    const isGh = isGreenhouseTask(task);
    const isLv = isLeverTask(task);
    if (platformFilter === 'greenhouse') return isGh;
    if (platformFilter === 'lever') return isLv;
    if (platformFilter === 'other') return !isGh && !isLv;
    return true;
};

function LeadTasksView() {
    const location = useLocation();
    const navigate = useNavigate();
    const { leadId } = useParams();
    const { user } = React.useContext(AuthContext);

    // Get initial data from navigation state
    const initialLead = location.state?.lead;
    const initialDate = location.state?.selectedDate || new Date().toISOString().split('T')[0];

    const [selectedDate, setSelectedDate] = useState(initialDate);
    const [lead, setLead] = useState(initialLead);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updatingTask, setUpdatingTask] = useState(null);
    const [expandedScoreDetails, setExpandedScoreDetails] = useState(new Set());
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [statusModal, setStatusModal] = useState({ show: false, taskId: null, newStatus: '' });
    const [confirmModal, setConfirmModal] = useState({ show: false, taskId: null });
    const [notRelevantModal, setNotRelevantModal] = useState({ show: false, taskId: null, reason: '', customReason: '' });
    const [genericConfirmModal, setGenericConfirmModal] = useState({ show: false, taskId: null, newStatus: '' });
    const [emailReceived, setEmailReceived] = useState(null); // null, 'yes', 'no'
    const [emailSubject, setEmailSubject] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [deleteTaskTarget, setDeleteTaskTarget] = useState(null);
    const [isDeletingTask, setIsDeletingTask] = useState(false);

    // Non-admin: tracks which task is currently being shown (0-indexed)
    const [currentTaskIndex, setCurrentTaskIndex] = useState(0);

    // Non-admin: whether the full pagination list is expanded
    const [paginationExpanded, setPaginationExpanded] = useState(false);

    // Shared: active status filter (null = show all)
    const [statusFilter, setStatusFilter] = useState(null);

    // Shared: platform filter for Auto Apply tasks (all | greenhouse | lever | other)
    const [platformFilter, setPlatformFilter] = useState('all');

    const applyTaskFilters = (tasks) => {
        let result = tasks || [];
        if (statusFilter) {
            result = result.filter(t => t.status?.toUpperCase() === statusFilter.toUpperCase());
        }
        if (platformFilter !== 'all') {
            result = result.filter(t => matchesPlatformFilter(t, platformFilter));
        }
        return result;
    };

    const handleStatCardClick = (status) => {
        const next = statusFilter === status ? null : status;
        setStatusFilter(next);
        setCurrentTaskIndex(0); // reset to first task whenever filter changes
    };

    const handlePlatformFilterClick = (platform) => {
        setPlatformFilter(platform);
        setCurrentTaskIndex(0);
    };

    // Non-admin: tracks which task's job description is expanded (by task id)
    const [expandedJobDesc, setExpandedJobDesc] = useState(new Set());
    const toggleJobDesc = (taskId) => setExpandedJobDesc(prev => {
        const next = new Set(prev);
        if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
        return next;
    });

    // Check if user has admin access
    const isAdmin = user && (user.is_superuser || user.role_name === 'Admin' || user.role_name === 'ADMIN' || user.role_name === 'Super Admin' || user.role_name === 'Associate admin' || user.role_name === 'Associate Admin');
    const isOps = user && (user.role_name === 'User' || user.role_name === 'Manager' || user.role_name === 'Associate');

    // Fetch tasks when date changes
    useEffect(() => {
        const controller = new AbortController();
        if (selectedDate && leadId) {
            fetchLeadTasks(controller.signal);
        }
        return () => controller.abort();
    }, [selectedDate, leadId]);

    const fetchLeadTasks = async (abortSignal) => {
        setLoading(true);
        setError('');
        try {
            // Pass lead_id so backend uses the fast DETAIL MODE (one lead, batch job fetch).
            const url = `/api/tasks/?date=${selectedDate}&lead_id=${leadId}`;
            const response = await fetch(url, { signal: abortSignal });
            const data = await response.json();

            if (response.ok) {
                // Backend now returns only this lead in the leads array.
                const foundLead = data.leads?.[0];
                if (foundLead) {
                    setLead(foundLead);
                    setError('');
                } else {
                    setError('No tasks found for this lead on the selected date');
                    setLead({
                        ...lead,
                        tasks: [],
                        total: 0,
                        pending: 0,
                        inProgress: 0,
                        completed: 0,
                        alreadyApplied: 0,
                        notRelevant: 0,
                        jobNotFound: 0
                    });
                }
            } else {
                setError(data.error || 'Failed to fetch tasks');
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('Error fetching tasks:', err);
            setError('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };


    const goToPreviousDay = () => {
        const [y, m, d] = selectedDate.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        date.setDate(date.getDate() - 1);

        const newY = date.getFullYear();
        const newM = String(date.getMonth() + 1).padStart(2, '0');
        const newD = String(date.getDate()).padStart(2, '0');
        setSelectedDate(`${newY}-${newM}-${newD}`);
    };

    const goToNextDay = () => {
        const [y, m, d] = selectedDate.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        date.setDate(date.getDate() + 1);

        const newY = date.getFullYear();
        const newM = String(date.getMonth() + 1).padStart(2, '0');
        const newD = String(date.getDate()).padStart(2, '0');
        setSelectedDate(`${newY}-${newM}-${newD}`);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const toggleScoreDetails = (taskId) => {
        const newExpanded = new Set(expandedScoreDetails);
        if (newExpanded.has(taskId)) {
            newExpanded.delete(taskId);
        } else {
            newExpanded.add(taskId);
        }
        setExpandedScoreDetails(newExpanded);
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    useEffect(() => {
        if (!screenshot) {
            setScreenshotPreviewUrl('');
            return;
        }

        const objectUrl = URL.createObjectURL(screenshot);
        setScreenshotPreviewUrl(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [screenshot]);

    const handleScreenshotFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert('File size exceeds 5MB limit');
            e.target.value = '';
            return;
        }

        setScreenshot(file);
    };

    const renderScreenshotPreview = () => {
        if (!screenshotPreviewUrl || !screenshot) return null;

        const isPdf = screenshot.type === 'application/pdf' || screenshot.name.toLowerCase().endsWith('.pdf');

        return (
            <div className="mt-4 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 px-3 py-2 border-b">Preview</p>
                {isPdf ? (
                    <iframe src={screenshotPreviewUrl} title="PDF Preview" className="w-full" style={{ height: '300px' }} />
                ) : (
                    <img src={screenshotPreviewUrl} alt="Screenshot preview" className="w-full object-contain max-h-64" />
                )}
            </div>
        );
    };

    // Helper function to recalculate summary counts after status change
    const recalculateSummary = (tasks) => {
        const summary = {
            total: tasks.length,
            pending: 0,
            inProgress: 0,
            completed: 0,
            awaitingEmail: 0,
            alreadyApplied: 0,
            notRelevant: 0,
            jobNotFound: 0
        };

        tasks.forEach(task => {
            const status = task.status.toUpperCase();
            if (status === 'PENDING') summary.pending++;
            else if (status === 'IN_PROGRESS' || status === 'STARTED') summary.inProgress++;
            else if (status === 'AWAITING EMAIL') summary.awaitingEmail++;
            else if (status === 'COMPLETED') summary.completed++;
            else if (status === 'ALREADY_APPLIED') summary.alreadyApplied++;
            else if (status === 'NOT_RELEVANT') summary.notRelevant++;
            else if (status === 'JOB_NOT_FOUND') summary.jobNotFound++;
        });

        return summary;
    };

    const updateTaskStatus = async (taskId, newStatus, isEmailReceivedValue, screenshotFile, remarks, emailSubjectValue) => {
        setUpdatingTask(taskId);

        try {
            const formData = new FormData();
            formData.append('taskId', taskId);
            formData.append('status', newStatus);

            if (screenshotFile) {
                formData.append('screenshot', screenshotFile);
            }
            if (isEmailReceivedValue !== undefined) {
                formData.append('is_email_received', isEmailReceivedValue);
            }
            if (remarks) {
                formData.append('remarks', remarks);
            }
            if (emailSubjectValue) {
                formData.append('email_subject', emailSubjectValue);
            }

            // GET CSRF
            const csrf = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))?.split('=')[1];

            // STEP 1: Send update to backend
            const response = await fetch('/api/admin/update-task-status/', {
                method: 'POST',
                headers: {
                    ...(csrf && { 'X-CSRFToken': csrf })
                },
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                // STEP 2: Update UI only on success
                const taskIndex = lead.tasks.findIndex(t => t.id === taskId);
                if (taskIndex !== -1) {
                    const updatedTasks = [...lead.tasks];
                    updatedTasks[taskIndex] = {
                        ...updatedTasks[taskIndex],
                        status: newStatus,
                        is_email_received: isEmailReceivedValue !== undefined ? isEmailReceivedValue : updatedTasks[taskIndex].is_email_received,
                        remarks: remarks !== undefined ? remarks : updatedTasks[taskIndex].remarks,
                        email_subject: emailSubjectValue !== undefined ? emailSubjectValue : updatedTasks[taskIndex].email_subject
                    };

                    const newSummary = recalculateSummary(updatedTasks);

                    setLead({
                        ...lead,
                        tasks: updatedTasks,
                        ...newSummary
                    });

                    // For non-admins, move to the next task if available
                    if (!isAdmin) {
                        const filteredTasksBefore = applyTaskFilters(lead.tasks);
                        const currentTask = filteredTasksBefore[currentTaskIndex];

                        // Only auto-progress if the task being updated is the one currently on screen
                        if (currentTask && currentTask.id === taskId) {
                            const filteredTasksAfter = applyTaskFilters(updatedTasks);

                            const taskLeftFilter = statusFilter && newStatus.toUpperCase() !== statusFilter.toUpperCase();

                            if (!taskLeftFilter) {
                                // Task is still in the filtered list (or no filter), so we must increment to see next
                                if (currentTaskIndex < filteredTasksAfter.length - 1) {
                                    setCurrentTaskIndex(prev => prev + 1);
                                }
                            } else {
                                // Task left the filtered list. Next task is now at same index.
                                // Adjust only if we were at the end of the list.
                                if (currentTaskIndex >= filteredTasksAfter.length && filteredTasksAfter.length > 0) {
                                    setCurrentTaskIndex(filteredTasksAfter.length - 1);
                                }
                            }
                        }
                    }
                }
                showToast('Task status updated successfully!', 'success');
            } else {
                setError(data.error || 'Failed to update task status');
                showToast(data.error || 'Failed to update task status', 'error');
            }
        } catch (err) {
            console.error('Error updating task status:', err);
            setError('Failed to update task status');
            showToast('Failed to update task status', 'error');
        } finally {
            setUpdatingTask(null);
            setUploading(false);
        }
    };

    const handleStatusDropdownChange = (taskId, status) => {
        setScreenshot(null);
        setEmailSubject('');
        if (status === 'COMPLETED') {
            setStatusModal({ show: true, taskId, newStatus: 'COMPLETED' });
            setEmailReceived(null);
        } else if (status === 'NOT_RELEVANT') {
            setNotRelevantModal({ show: true, taskId, reason: '', customReason: '' });
        } else {
            setGenericConfirmModal({ show: true, taskId, newStatus: status });
        }
    };

    const handleMarkAsCompleted = (taskId) => {
        setEmailSubject('');
        setConfirmModal({ show: true, taskId });
    };

    const submitConfirmModal = async () => {
        if (!emailSubject || emailSubject.trim().length <= 10) {
            alert('Email subject is mandatory and must be more than 10 characters.');
            return;
        }
        const taskId = confirmModal.taskId;
        const subjectToSend = emailSubject.trim();
        setConfirmModal({ show: false, taskId: null });
        setEmailSubject('');
        await updateTaskStatus(taskId, 'COMPLETED', true, undefined, undefined, subjectToSend);
    };

    const submitStatusModal = async () => {
        if (emailReceived === null) {
            alert('Please select if email was received.');
            return;
        }

        if (emailReceived === 'no' && !screenshot) {
            alert('Screenshot is mandatory when email is not received.');
            return;
        }

        if (emailReceived === 'yes') {
            if (!emailSubject || emailSubject.trim().length <= 10) {
                alert('Email subject is mandatory and must be more than 10 characters.');
                return;
            }
        }

        setUploading(true);
        const finalStatus = emailReceived === 'yes' ? 'COMPLETED' : 'AWAITING EMAIL';
        const isEmailReceivedValue = emailReceived === 'yes';

        await updateTaskStatus(
            statusModal.taskId,
            finalStatus,
            isEmailReceivedValue,
            screenshot,
            undefined,
            emailReceived === 'yes' ? emailSubject.trim() : undefined
        );

        setStatusModal({ show: false, taskId: null, newStatus: '' });
        setEmailReceived(null);
        setEmailSubject('');
        setUploading(false);
    };

    const submitNotRelevantModal = async () => {
        const { taskId, reason, customReason } = notRelevantModal;
        if (!reason) {
            alert('Please select a reason.');
            return;
        }
        if (reason === 'Other' && !customReason.trim()) {
            alert('Please specify the reason.');
            return;
        }

        const finalRemarks = reason === 'Other' ? customReason.trim() : reason;

        setUploading(true);
        await updateTaskStatus(taskId, 'NOT_RELEVANT', undefined, undefined, finalRemarks);
        setNotRelevantModal({ show: false, taskId: null, reason: '', customReason: '' });
        setScreenshot(null);
        setUploading(false);
    };

    const getStatusColor = (status) => {
        switch (status.toUpperCase()) {
            case 'PENDING':
                return 'bg-yellow-200 text-yellow-900';
            case 'IN_PROGRESS':
            case 'STARTED':
                return 'bg-blue-200 text-blue-900';
            case 'COMPLETED':
                return 'bg-green-200 text-green-900';
            case 'AWAITING EMAIL':
                return 'bg-purple-200 text-purple-900';
            case 'ALREADY_APPLIED':
                return 'bg-teal-200 text-teal-900';
            case 'NOT_RELEVANT':
                return 'bg-red-200 text-red-900';
            case 'JOB_NOT_FOUND':
                return 'bg-orange-200 text-orange-900';
            case 'NEED_OTP':
                return 'bg-pink-200 text-pink-900';
            default:
                return 'bg-gray-200 text-gray-800';
        }
    };

    const getExperienceMatchColor = (leadExperience, taskExperience) => {
        // If task experience is Unknown, return orange
        const taskExpRaw = taskExperience ? taskExperience.toString().trim() : '';
        if (!taskExpRaw || taskExpRaw.toLowerCase() === 'unknown' || taskExpRaw.toLowerCase() === 'not available') {
            return 'bg-orange-300 text-orange-800';
        }

        // If lead experience is not available, return gray
        if (leadExperience === null || leadExperience === undefined) {
            return 'bg-gray-300 text-gray-800';
        }

        const experienceStr = taskExpRaw.toLowerCase();
        let minExp = 0;
        let maxExp = 100;
        let matched = false;

        // 1. Try to parse range (e.g. "0-4", "5-7", "3 to 5")
        const rangeMatch = experienceStr.match(/(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)/);
        if (rangeMatch) {
            minExp = parseFloat(rangeMatch[1]);
            maxExp = parseFloat(rangeMatch[2]);
            matched = true;
        }
        // 2. Try to parse "X+" or "X plus" or "at least X" or "X and up"
        else if (experienceStr.includes('+') || experienceStr.includes('plus') || experienceStr.includes('at least') || experienceStr.includes('and up')) {
            const numMatch = experienceStr.match(/(\d+(?:\.\d+)?)/);
            if (numMatch) {
                minExp = parseFloat(numMatch[1]);
                maxExp = 100;
                matched = true;
            }
        }
        // 3. Try to parse as a single number (e.g., "5", "7.5", "60")
        else {
            const parsed = parseFloat(experienceStr);
            if (!isNaN(parsed)) {
                minExp = parsed;
                maxExp = 100; // Assume single number means "at least X years"
                matched = true;
            }
        }

        if (!matched) {
            // Any other pattern that doesn't include a number or a range
            return 'bg-orange-300 text-orange-800';
        }

        // Convert months to years if > 30 (e.g., 60 becomes 5)
        if (minExp > 30) minExp = minExp / 12;
        if (maxExp > 30 && maxExp !== 100) maxExp = maxExp / 12;

        const leadExp = Number(leadExperience);
        // Use maxExp as the benchmark (or minExp for X+) to represent "task.experience"
        const taskExpVal = (maxExp === 100) ? minExp : maxExp;
        const diff = leadExp - taskExpVal;

        // 1. GREEN: task.experience - 1 <= lead.experience <= task.experience + 1
        if (leadExp >= (taskExpVal - 1) && leadExp <= (taskExpVal + 1)) {
            return 'bg-green-300 text-green-800';
        }

        // 2. YELLOW: -3 < difference < +3 (relative to task experience)
        // This includes cases where lead has slightly more experience, as long as it's within 3 years
        if (diff > -3 && diff < 3) {
            return 'bg-yellow-300 text-yellow-800';
        }

        // 3. RED: other than that
        return 'bg-red-300 text-red-800';
    };

    const deleteTask = async (task) => {
        const taskId = task.id;
        setIsDeletingTask(true);
        // Store the task for rollback if needed
        const taskIndex = lead.tasks.findIndex(t => t.id === taskId);
        const deletedTask = taskIndex !== -1 ? { ...lead.tasks[taskIndex] } : null;

        try {
            // STEP 1: Optimistic delete (instant UI update)
            if (taskIndex !== -1) {
                const updatedTasks = lead.tasks.filter(t => t.id !== taskId);
                const newSummary = recalculateSummary(updatedTasks);

                setLead({
                    ...lead,
                    tasks: updatedTasks,
                    ...newSummary
                });
            }

            // STEP 2: Send delete request to backend
            const response = await fetch('/api/admin/delete-task/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    taskId,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                // Show success toast immediately
                showToast(data.message || 'Task deleted successfully!', 'success');
                setDeleteTaskTarget(null);
                // No background sync - trust the optimistic update
            } else {
                // Rollback optimistic delete on error
                if (deletedTask && taskIndex !== -1) {
                    const rolledBackTasks = [...lead.tasks];
                    rolledBackTasks.splice(taskIndex, 0, deletedTask);
                    const rolledBackSummary = recalculateSummary(rolledBackTasks);

                    setLead({
                        ...lead,
                        tasks: rolledBackTasks,
                        ...rolledBackSummary
                    });
                }

                setError(data.error || 'Failed to delete task');
                showToast(data.error || 'Failed to delete task', 'error');
            }
        } catch (err) {
            // Rollback optimistic delete on error
            if (deletedTask && taskIndex !== -1) {
                const rolledBackTasks = [...lead.tasks];
                rolledBackTasks.splice(taskIndex, 0, deletedTask);
                const rolledBackSummary = recalculateSummary(rolledBackTasks);

                setLead({
                    ...lead,
                    tasks: rolledBackTasks,
                    ...rolledBackSummary
                });
            }

            console.error('Error deleting task:', err);
            setError('Failed to delete task');
            showToast('Failed to delete task', 'error');
        } finally {
            setIsDeletingTask(false);
        }
    };

    // ── Non-admin task navigation helpers ────────────────────────────────────
    // A task at `index` is unlocked when either it's the first task,
    // or the PREVIOUS task has moved past PENDING / null.
    const isTaskUnlocked = (index) => {
        if (index === 0) return true;
        const prev = lead?.tasks?.[index - 1];
        const s = prev?.status?.toUpperCase();
        return !!s && s !== 'PENDING';
    };

    // Returns a sliding window of up to 7 page indices centred on `current`.
    const getVisiblePageNumbers = (current, total, windowSize = 7) => {
        let start = Math.max(0, current - Math.floor(windowSize / 2));
        let end = Math.min(total - 1, start + windowSize - 1);
        // Shift start left if the window is smaller than desired near the end
        if (end - start + 1 < windowSize) {
            start = Math.max(0, end - windowSize + 1);
        }
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };


    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-semibold">Loading tasks...</p>
                </div>
            </div>
        );
    }

    if (!lead) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-md max-w-md w-full text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
                    <p className="text-gray-600 mb-6">{error || 'No lead data available for this date.'}</p>
                    <button
                        onClick={() => navigate('/view-tasks', { state: { date: selectedDate } })}
                        className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-150 active:scale-95 cursor-pointer"
                    >
                        Back to Tasks
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/view-tasks', { state: { date: selectedDate } })}
                    className="mb-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Tasks
                </button>

                {/* Date Navigation */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Select Date</h2>
                    <div className="flex items-center justify-center space-x-4">
                        <button
                            onClick={goToPreviousDay}
                            className="p-2 text-gray-400 hover:text-gray-600"
                            disabled={loading}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <div className="flex flex-col items-center">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select Date:
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={loading}
                            />
                        </div>

                        <button
                            onClick={goToNextDay}
                            className="p-2 text-gray-400 hover:text-gray-600"
                            disabled={loading}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {loading && (
                        <div className="mt-4 text-center text-blue-600 text-sm">
                            Loading tasks...
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 text-center text-red-600 text-sm">
                            {error}
                        </div>
                    )}
                </div>

                {/* Lead Info Header */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{lead.name}</h1>
                            <p className="text-sm text-gray-600">{lead.email}</p>
                            {lead.targetRole && (
                                <p className="text-xs text-gray-500 font-medium mt-0.5">
                                    Domain: {lead.targetRole}
                                </p>
                            )}
                            {lead.agent && (
                                <p className="text-xs text-blue-600 mt-1">
                                    Agent: {lead.agent.name} ({lead.agent.email})
                                </p>
                            )}
                            {lead.clientPreferences && Object.keys(lead.clientPreferences).length > 0 && (
                                <div className="mt-2 text-xs text-gray-700 bg-gray-50 p-2 rounded border border-gray-200">
                                    <strong>Client Preferences:</strong>
                                    <div className="mt-1 space-y-1">
                                        {Object.entries(lead.clientPreferences).map(([key, val]) => (
                                            <div key={key}>
                                                <span className="font-semibold text-gray-600">{key.replace(/_/g, ' ')}:</span>{' '}
                                                {Array.isArray(val) ? val.join(', ') : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Upload Gmail Screenshot — reusable component */}
                        <GmailScreenshotUpload
                            leadId={lead.id}
                            leadName={lead.name}
                            selectedDate={selectedDate}
                        />
                    </div>

                    {/* Task Stats */}
                    <div className="grid grid-cols-4 md:grid-cols-9 gap-4 mt-4">
                        {[
                            { label: 'TOTAL', count: lead.total || 0, bg: 'bg-gray-200', text: 'text-gray-900', sub: 'text-gray-700', status: null },
                            { label: 'PENDING', count: lead.pending || 0, bg: 'bg-yellow-200', text: 'text-yellow-900', sub: 'text-yellow-800', status: 'PENDING' },
                            { label: 'IN PROGRESS', count: lead.inProgress || 0, bg: 'bg-blue-200', text: 'text-blue-900', sub: 'text-blue-800', status: 'IN_PROGRESS' },
                            { label: 'AWAITING EMAIL', count: lead.awaitingEmail || 0, bg: 'bg-purple-200', text: 'text-purple-900', sub: 'text-purple-800', status: 'AWAITING EMAIL' },
                            { label: 'COMPLETED', count: lead.completed || 0, bg: 'bg-green-200', text: 'text-green-900', sub: 'text-green-800', status: 'COMPLETED' },
                            { label: 'ALREADY APPLIED', count: lead.alreadyApplied || 0, bg: 'bg-teal-200', text: 'text-teal-900', sub: 'text-teal-800', status: 'ALREADY_APPLIED' },
                            { label: 'NOT RELEVANT', count: lead.notRelevant || 0, bg: 'bg-red-200', text: 'text-red-900', sub: 'text-red-800', status: 'NOT_RELEVANT' },
                            { label: 'JOB NOT FOUND', count: lead.jobNotFound || 0, bg: 'bg-orange-200', text: 'text-orange-900', sub: 'text-orange-800', status: 'JOB_NOT_FOUND' },
                            { label: 'NEED OTP/AUTH', count: lead.needOtp || 0, bg: 'bg-pink-200', text: 'text-pink-900', sub: 'text-pink-800', status: 'NEED_OTP' },
                        ].map(({ label, count, bg, text, sub, status }) => {
                            const isActive = statusFilter === status;
                            return (
                                <div
                                    key={label}
                                    onClick={() => handleStatCardClick(status)}
                                    className={`${bg} p-3 rounded-lg text-center cursor-pointer select-none transition-all duration-150 ${isActive
                                        ? 'ring-4 ring-offset-1 ring-gray-700 scale-105 shadow-lg'
                                        : 'hover:scale-105 hover:shadow-md'
                                        }`}
                                >
                                    <div className={`text-lg font-semibold ${text}`}>{count}</div>
                                    <div className={`text-xs ${sub}`}>{label}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Completion Progress</span>
                            <span>{lead.total > 0 ? Math.round(((lead.completed + (lead.awaitingEmail || 0) + lead.alreadyApplied + lead.notRelevant + lead.jobNotFound) / lead.total) * 100) : 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${lead.total > 0 ? Math.round(((lead.completed + (lead.awaitingEmail || 0) + lead.alreadyApplied + lead.notRelevant + lead.jobNotFound) / lead.total) * 100) : 0}%`
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Individual Tasks */}
                {loading || !lead?.tasks ? (
                    <div className="bg-white rounded-lg shadow p-6 text-center">
                        <p className="text-gray-600">Loading tasks...</p>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-lg shadow p-6 text-center">
                        <p className="text-red-500 font-medium">{error}</p>
                        <button onClick={() => fetchLeadTasks()} className="mt-2 text-blue-600 hover:text-blue-800 text-sm">
                            Try Again
                        </button>
                    </div>
                ) : lead.tasks.length > 0 ? (
                    (() => {
                        // Status-only list used for platform button counts
                        const statusFilteredTasks = statusFilter
                            ? lead.tasks.filter(t => t.status?.toUpperCase() === statusFilter.toUpperCase())
                            : lead.tasks;

                        const platformCounts = {
                            all: statusFilteredTasks.length,
                            greenhouse: statusFilteredTasks.filter(t => matchesPlatformFilter(t, 'greenhouse')).length,
                            lever: statusFilteredTasks.filter(t => matchesPlatformFilter(t, 'lever')).length,
                            other: statusFilteredTasks.filter(t => matchesPlatformFilter(t, 'other')).length,
                        };

                        // Derive filtered task list (shared by admin + non-admin)
                        const filteredTasks = applyTaskFilters(lead.tasks);

                        const platformFilterButtons = (
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                {[
                                    { key: 'all', label: 'All' },
                                    { key: 'greenhouse', label: 'Greenhouse' },
                                    { key: 'lever', label: 'Lever' },
                                    { key: 'other', label: 'Other' },
                                ].map(({ key, label }) => {
                                    const isActive = platformFilter === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => handlePlatformFilterClick(key)}
                                            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${
                                                isActive
                                                    ? 'bg-cyan-600 text-white border-cyan-700 shadow'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {label} ({platformCounts[key]})
                                        </button>
                                    );
                                })}
                            </div>
                        );

                        return isAdmin ? (
                            /* ═══════════════════════════════════════════════════
                               ADMIN VIEW — show filtered tasks
                               ═══════════════════════════════════════════════════ */
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-md font-semibold text-gray-800">Individual Tasks</h4>
                                    {statusFilter && (
                                        <span className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                            Filtered: {statusFilter.replace(/_/g, ' ')}
                                            <button onClick={() => setStatusFilter(null)} className="ml-1 text-blue-600 hover:text-blue-900 font-bold">✕</button>
                                        </span>
                                    )}
                                </div>
                                {platformFilterButtons}
                                <div className="space-y-3">
                                    {filteredTasks.length === 0 ? (
                                        <p className="text-center text-gray-500 py-6">
                                            {statusFilter || platformFilter !== 'all'
                                                ? <>No tasks match the current filter{statusFilter ? <> (status: <strong>{statusFilter.replace(/_/g, ' ')}</strong>)</> : null}{platformFilter !== 'all' ? <> (platform: <strong>{platformFilter}</strong>)</> : null}.</>
                                                : 'No tasks found.'}
                                        </p>
                                    ) : filteredTasks.map((task, taskIndex) => (
                                        <div key={task.id} className={`rounded-lg p-4 ${getStatusColor(task.status)}`}>
                                            <div className="flex justify-between items-start mb-3">
                                                {/* Serial Number */}
                                                <div className='flex items-center justify-center bg-gray-300 rounded-full w-6 h-6 flex-shrink-0 mr-3 mb-2 md:mb-0'>
                                                    <div className="text-gray-900 font-medium text-xs">
                                                        {taskIndex + 1}
                                                    </div>
                                                </div>
                                                <div className="flex-1">
                                                    {/* Task Title */}
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <h5 className="font-medium text-gray-900">
                                                            {(task.jobTitle || task.title || 'Job Title')} at {(task.company || 'Company')} {task.jobId ? `(${task.jobId})` : ''}
                                                        </h5>
                                                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                                                            {lead.targetRole || 'DevOps'}
                                                        </span>
                                                    </div>

                                                    {/* Match Score and Location */}
                                                    <div className="flex items-center gap-4 mb-2 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
                                                                Match Score: {task.score ? task.score : 0}
                                                            </span>
                                                            <button
                                                                onClick={() => toggleScoreDetails(task.id)}
                                                                className="text-blue-600 hover:text-blue-800 font-medium"
                                                            >
                                                                🔍 {expandedScoreDetails.has(task.id) ? 'Hide Details' : 'Score Details'}
                                                            </button>
                                                        </div>
                                                        <div className="flex items-center text-gray-600">
                                                            <span>📍 {task.location || 'Not Available'}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg border-2 border-cyan-300 hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center gap-2">
                                                                🌐 Source: {task.source || 'Not Available'}
                                                            </span>
                                                        </div>
                                                        <div className={`flex items-center px-3 py-1 rounded-full font-medium border-2 ${getExperienceMatchColor(lead.experience, task.experience)}`}>
                                                            <span>Experience Required: {task.experience || 'Not Available'}, client experience : {lead.experience}</span>
                                                        </div>
                                                    </div>

                                                    {/* Score Details */}
                                                    {expandedScoreDetails.has(task.id) && (
                                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                                                            <h6 className="font-semibold text-blue-900 mb-2">Scoring Details</h6>
                                                            <div className="text-sm text-blue-800">
                                                                <p className="mb-2"><strong>AI Reasoning:</strong></p>
                                                                <p className="text-blue-700 leading-relaxed">
                                                                    {task.reasoning || task.description || "No AI reasoning available for this job match."}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Due Date and Email Status */}
                                                    <div className="flex items-center gap-4 mb-3 text-sm">
                                                        <div className="flex items-center text-red-600">
                                                            <span>📅 Overdue {task.dueDate ? formatDate(task.dueDate) : '21/08/2025'}</span>
                                                        </div>
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => window.open(`/task-details/${task.id}`, '_blank')}
                                                            className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 transition-colors"
                                                        >
                                                            ℹ️ View Details
                                                        </button>
                                                        {task.jobUrl ? (
                                                            <a
                                                                href={task.jobUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded hover:bg-green-200 transition-colors"
                                                            >
                                                                🔗 View Job
                                                            </a>
                                                        ) : (
                                                            <span className="px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded cursor-not-allowed">
                                                                🔗 View Job
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => window.open(`/leads/${lead.id}`, '_blank')}
                                                            className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded hover:bg-purple-200 transition-colors"
                                                        >
                                                            👤 View Lead
                                                        </button>
                                                        {task.has_autofill_data && (
                                                            <button
                                                                onClick={() => {
                                                                    const targetUrl = task.jobUrl
                                                                        ? new URL(task.jobUrl, window.location.origin)
                                                                        : new URL(window.location.origin);
                                                                    targetUrl.searchParams.set('task_id', task.id);
                                                                    targetUrl.searchParams.set('platform', 'greenhouse');
                                                                    window.open(targetUrl.toString(), '_blank', 'noopener,noreferrer');
                                                                }}
                                                                className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded hover:bg-emerald-200 transition-colors"
                                                            >
                                                                ⚡ Auto Apply
                                                            </button>
                                                        )}
                                                        {isAdmin && user.role_name !== 'Associate admin' && user.role_name !== 'Associate Admin' && (
                                                            <button
                                                                onClick={() => setDeleteTaskTarget(task)}
                                                                className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 transition-colors"
                                                            >
                                                                🗑️ Delete Task
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Status Dropdown */}
                                                <div className="ml-4 flex flex-col items-end">
                                                    <div className={`px-3 py-1 rounded-full text-xs font-medium mb-1 ${getStatusColor(task.status)}`}>
                                                        {task.status}
                                                    </div>
                                                    {task.remarks && (
                                                        <div className="text-xs text-gray-500 italic mb-2">Remarks: {task.remarks}</div>
                                                    )}
                                                    {task.caScreenshotUrl && (
                                                        <a
                                                            href={task.caScreenshotUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold mb-2"
                                                        >
                                                            📸 View CA Screenshot
                                                        </a>
                                                    )}

                                                    {task.status.toUpperCase() === 'COMPLETED' ? (
                                                        <div className="flex flex-col gap-1">
                                                            <div className="text-xs text-green-600 font-bold">✓ Task Locked</div>
                                                            {task.remarks && <div className="text-xs text-gray-500 italic">Remarks: {task.remarks}</div>}
                                                        </div>
                                                    ) : task.status.toUpperCase() === 'AWAITING EMAIL' ? (
                                                        <button
                                                            onClick={() => handleMarkAsCompleted(task.id)}
                                                            disabled={updatingTask === task.id}
                                                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                                        >
                                                            Mark as Completed
                                                        </button>
                                                    ) : (
                                                        <select
                                                            value={task.status}
                                                            onChange={(e) => handleStatusDropdownChange(task.id, e.target.value)}
                                                            disabled={updatingTask === task.id}
                                                            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        >
                                                            <option value="PENDING">PENDING</option>
                                                            <option value="IN_PROGRESS">IN PROGRESS</option>
                                                            <option value="COMPLETED">COMPLETED</option>
                                                            <option value="ALREADY_APPLIED">ALREADY APPLIED</option>
                                                            <option value="NOT_RELEVANT">NOT RELEVANT</option>
                                                            <option value="JOB_NOT_FOUND">JOB NOT FOUND</option>
                                                            <option value="NEED_OTP">NEED OTP/AUTHENTICATION</option>
                                                        </select>
                                                    )}

                                                    {updatingTask === task.id && (
                                                        <div className="mt-1 text-xs text-blue-500">Updating...</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            /* NON-ADMIN VIEW — one task at a time + pagination */
                            (() => {
                                const task = filteredTasks[currentTaskIndex] || filteredTasks[0];
                                if (!task) return (
                                    <div className="bg-white rounded-lg shadow p-6 text-center">
                                        {platformFilterButtons}
                                        {(statusFilter || platformFilter !== 'all') && (
                                            <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
                                                {statusFilter && (
                                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                                        Status: {statusFilter.replace(/_/g, ' ')}
                                                        <button onClick={() => { setStatusFilter(null); setCurrentTaskIndex(0); }} className="ml-1 text-blue-600 hover:text-blue-900 font-bold">✕</button>
                                                    </span>
                                                )}
                                                {platformFilter !== 'all' && (
                                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-800">
                                                        Platform: {platformFilter}
                                                        <button onClick={() => handlePlatformFilterClick('all')} className="ml-1 text-cyan-600 hover:text-cyan-900 font-bold">✕</button>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <p className="text-gray-500">No tasks match this filter.</p>
                                    </div>
                                );
                                const total = filteredTasks.length;
                                const isFirst = currentTaskIndex === 0;
                                const isLast = currentTaskIndex === total - 1;
                                // For non-admin, unlock logic uses original index in lead.tasks
                                const origIndex = lead.tasks.indexOf(task);
                                const nextTask = filteredTasks[currentTaskIndex + 1];
                                const nextOrigIndex = nextTask ? lead.tasks.indexOf(nextTask) : -1;
                                const nextUnlocked = !isLast && (isTaskUnlocked(nextOrigIndex) || nextOrigIndex < origIndex);
                                const terminalStatuses = ['COMPLETED', 'ALREADY_APPLIED', 'NOT_RELEVANT', 'JOB_NOT_FOUND'];
                                const statusUpper = task.status?.toUpperCase() || '';
                                const isTerminal = terminalStatuses.includes(statusUpper);

                                return (
                                    <div className="bg-white rounded-lg shadow p-6">
                                        {platformFilterButtons}
                                        {/* Filter banner */}
                                        {(statusFilter || platformFilter !== 'all') && (
                                            <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                                                <span className="text-xs font-semibold text-blue-800">
                                                    Showing
                                                    {statusFilter ? `: ${statusFilter.replace(/_/g, ' ')}` : ''}
                                                    {platformFilter !== 'all' ? `${statusFilter ? ' · ' : ': '}${platformFilter}` : ''}
                                                    {' '}({total} task{total !== 1 ? 's' : ''})
                                                </span>
                                                <button
                                                    onClick={() => { setStatusFilter(null); handlePlatformFilterClick('all'); }}
                                                    className="text-xs text-blue-600 hover:text-blue-900 font-bold"
                                                >
                                                    ✕ Clear filter
                                                </button>
                                            </div>
                                        )}
                                        {/* Task header */}
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="text-md font-semibold text-gray-800">
                                                Task {currentTaskIndex + 1} of {total}
                                            </h4>
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
                                                {task.status || 'PENDING'}
                                            </span>
                                        </div>

                                        {/* Task card */}
                                        <div className={`rounded-xl border-2 p-5 ${getStatusColor(task.status)}`}>
                                            {/* Job title */}
                                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                                <h5 className="text-lg font-bold text-gray-900">
                                                    {task.jobTitle || task.title || 'Job Title'} at {task.company || 'Company'}
                                                    {task.jobId ? <span className="ml-1 text-gray-500 text-sm font-normal">({task.jobId})</span> : null}
                                                </h5>
                                                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">{lead.targetRole || 'Not Available'}</span>
                                                {task.applyType && task.applyType !== 'not found' && (
                                                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">
                                                        {task.applyType}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Key metrics */}
                                            <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
                                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded font-medium">Match Score: {task.score || 0}</span>
                                                <span className="text-gray-600">📍 {task.location || 'Not Available'}</span>
                                                <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-3 py-1 rounded-lg font-semibold text-xs shadow border border-cyan-300">🌐 {task.source || 'Not Available'}</span>
                                                <span className={`px-3 py-1 rounded-full font-medium border-2 text-xs ${getExperienceMatchColor(lead.experience, task.experience)}`}>Exp Required: {task.experience || 'N/A'} | Client: {lead.experience}</span>
                                                <span className="text-red-600 text-xs">📅 Due: {task.dueDate ? formatDate(task.dueDate) : '—'}</span>
                                            </div>

                                            {/* AI Reasoning */}
                                            {(task.description || task.reasoning) && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                                    <p className="text-xs font-semibold text-blue-900 mb-1">🤖 AI Reasoning</p>
                                                    <p className="text-sm text-blue-800 leading-relaxed">{task.reasoning || task.description}</p>
                                                </div>
                                            )}

                                            {/* Job Description from DB */}
                                            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                                                <p className="text-xs font-semibold text-gray-700 mb-1">📄 Job Description</p>
                                                <p className={`text-sm text-gray-700 leading-relaxed whitespace-pre-line ${expandedJobDesc.has(task.id) ? '' : 'line-clamp-6'
                                                    }`}>
                                                    {task.jobDescription || 'No job description available for this task.'}
                                                </p>
                                                {task.jobDescription && (
                                                    <button
                                                        onClick={() => toggleJobDesc(task.id)}
                                                        className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                                                    >
                                                        {expandedJobDesc.has(task.id) ? '▲ Read less' : '▼ Read more'}
                                                    </button>
                                                )}
                                            </div> */}

                                            {/* Action buttons */}
                                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                                <button onClick={() => window.open(`/task-details/${task.id}`, '_blank')} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 transition-colors">ℹ️ View Details</button>
                                                {task.jobUrl ? (
                                                    <a href={task.jobUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded hover:bg-green-200 transition-colors">🔗 View Job</a>
                                                ) : (
                                                    <span className="px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded cursor-not-allowed">🔗 View Job</span>
                                                )}
                                                {task.has_autofill_data && (
                                                    <button
                                                    onClick={() => {
                                                        const targetUrl = task.jobUrl
                                                        ? new URL(task.jobUrl, window.location.origin)
                                                        : new URL(window.location.origin);
                                                        targetUrl.searchParams.set('task_id', task.id);
                                                        targetUrl.searchParams.set('platform', 'greenhouse');
                                                        window.open(targetUrl.toString(), '_blank', 'noopener,noreferrer');
                                                    }}
                                                    className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded hover:bg-emerald-200 transition-colors"
                                                    >
                                                        ⚡ Auto Apply
                                                    </button>
                                                )}
                                                <button onClick={() => window.open(`/leads/${lead.id}`, '_blank')} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded hover:bg-purple-200 transition-colors">👤 View Lead</button>
                                            </div>

                                            {/* Status control */}
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="text-sm font-medium text-gray-600">Status:</span>
                                                {isTerminal ? (
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(task.status)}`}>{task.status}</span>
                                                            <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-full border border-gray-300">🔒 Task Locked</span>
                                                        </div>
                                                        {task.remarks && <div className="text-xs text-gray-500 italic ml-2">Remarks: {task.remarks}</div>}
                                                    </div>
                                                ) : statusUpper === 'AWAITING EMAIL' ? (
                                                    <button onClick={() => handleMarkAsCompleted(task.id)} disabled={updatingTask === task.id} className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-60">Mark as Completed</button>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {[
                                                            { value: 'PENDING', label: 'Pending', activeClass: 'bg-yellow-400 text-yellow-900 border-yellow-500' },
                                                            { value: 'IN_PROGRESS', label: 'In Progress', activeClass: 'bg-blue-500 text-white border-blue-600' },
                                                            { value: 'COMPLETED', label: 'Completed', activeClass: 'bg-green-500 text-white border-green-600' },
                                                            { value: 'ALREADY_APPLIED', label: 'Already Applied', activeClass: 'bg-teal-500 text-white border-teal-600' },
                                                            { value: 'NOT_RELEVANT', label: 'Not Relevant', activeClass: 'bg-red-500 text-white border-red-600' },
                                                            { value: 'JOB_NOT_FOUND', label: 'Job Not Found', activeClass: 'bg-orange-500 text-white border-orange-600' },
                                                            { value: 'NEED_OTP', label: 'Need OTP/Auth', activeClass: 'bg-pink-500 text-white border-pink-600' },
                                                        ].map(({ value, label, activeClass }) => {
                                                            const isActive = (task.status || 'PENDING') === value;
                                                            return (
                                                                <button
                                                                    key={value}
                                                                    onClick={() => !isActive && handleStatusDropdownChange(task.id, value)}
                                                                    disabled={updatingTask === task.id || isActive}
                                                                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${isActive
                                                                        ? `${activeClass} cursor-default shadow-sm scale-105`
                                                                        : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200 hover:text-gray-700 cursor-pointer'
                                                                        }`}
                                                                >
                                                                    {label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {updatingTask === task.id && <span className="text-xs text-blue-500 ml-1">Updating...</span>}
                                            </div>
                                        </div>

                                        {/* Pagination bar */}
                                        <div className="mt-6">
                                            {/* Prev / Next row + expand toggle */}
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    {!isFirst && (
                                                        <button
                                                            onClick={() => setCurrentTaskIndex(idx => idx - 1)}
                                                            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold text-sm transition-colors"
                                                        >← Prev</button>
                                                    )}
                                                </div>

                                                {/* Current position indicator */}
                                                <span className="text-xs text-gray-500 font-medium">
                                                    Task {currentTaskIndex + 1} / {total}
                                                </span>

                                                <div className="flex items-center gap-2">
                                                    {!isLast && (
                                                        <button
                                                            onClick={() => nextUnlocked ? setCurrentTaskIndex(idx => idx + 1) : showToast('Please update the status of the current task first', 'error')}
                                                            title={nextUnlocked ? 'Next task' : 'Update current task status first'}
                                                            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${nextUnlocked ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                                                        >Next →</button>
                                                    )}
                                                    {/* Expand / Collapse toggle */}
                                                    <button
                                                        onClick={() => setPaginationExpanded(e => !e)}
                                                        title={paginationExpanded ? 'Collapse task list' : 'Expand task list'}
                                                        className="w-8 h-8 rounded-lg border-2 border-gray-300 bg-white hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-all text-base font-bold"
                                                    >
                                                        {paginationExpanded ? '−' : '⊞'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Number buttons */}
                                            <div className={`flex flex-wrap justify-center gap-2 mt-3 ${paginationExpanded ? '' : 'overflow-hidden'
                                                }`}>
                                                {(() => {
                                                    const statusStyles = {
                                                        PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-400 hover:bg-yellow-200',
                                                        IN_PROGRESS: 'bg-blue-100 text-blue-800 border-blue-400 hover:bg-blue-200',
                                                        STARTED: 'bg-blue-100 text-blue-800 border-blue-400 hover:bg-blue-200',
                                                        COMPLETED: 'bg-green-100 text-green-800 border-green-400 hover:bg-green-200',
                                                        AWAITING_EMAIL: 'bg-purple-100 text-purple-800 border-purple-400 hover:bg-purple-200',
                                                        'AWAITING EMAIL': 'bg-purple-100 text-purple-800 border-purple-400 hover:bg-purple-200',
                                                        ALREADY_APPLIED: 'bg-teal-100 text-teal-800 border-teal-400 hover:bg-teal-200',
                                                        NOT_RELEVANT: 'bg-red-100 text-red-800 border-red-400 hover:bg-red-200',
                                                        JOB_NOT_FOUND: 'bg-orange-100 text-orange-800 border-orange-400 hover:bg-orange-200',
                                                        NEED_OTP: 'bg-pink-100 text-pink-800 border-pink-400 hover:bg-pink-200',
                                                    };

                                                    // i = index in filteredTasks
                                                    // origI = index in lead.tasks (for unlock checks)
                                                    const toOrigIndex = (i) => lead.tasks.indexOf(filteredTasks[i]);

                                                    // Collapsed: sliding window of 7. Expanded: all unlocked filtered tasks only.
                                                    const indices = paginationExpanded
                                                        ? Array.from({ length: total }, (_, i) => i).filter(i => {
                                                            const origI = toOrigIndex(i);
                                                            return isTaskUnlocked(origI) || origI < toOrigIndex(currentTaskIndex);
                                                        })
                                                        : getVisiblePageNumbers(currentTaskIndex, total, 7);

                                                    return indices.map(i => {
                                                        const origI = toOrigIndex(i);
                                                        const canNavigate = isTaskUnlocked(origI) || origI < toOrigIndex(currentTaskIndex);
                                                        const isCurrent = i === currentTaskIndex;
                                                        // ✅ read status from filteredTasks[i], not lead.tasks[i]
                                                        const taskStatus = (filteredTasks[i]?.status || 'PENDING').toUpperCase();
                                                        const navigableStyle = statusStyles[taskStatus] || 'bg-gray-50 text-gray-700 border-gray-400 hover:bg-gray-100';
                                                        return (
                                                            <button key={i}
                                                                onClick={() => canNavigate ? setCurrentTaskIndex(i) : showToast('Please update the status of the current task first', 'error')}
                                                                title={canNavigate ? `Task ${i + 1} — ${taskStatus.replace(/_/g, ' ')}` : 'Update current task status first'}
                                                                className={`w-10 h-10 rounded-full text-sm font-bold transition-all duration-150 border-2 ${isCurrent
                                                                    ? 'bg-blue-600 text-white border-blue-700 shadow-md scale-110'
                                                                    : canNavigate
                                                                        ? navigableStyle
                                                                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                                                                    }`}
                                                            >{i + 1}</button>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()
                        );
                    })()
                ) : (
                    <div className="bg-white rounded-lg shadow p-6 text-center">
                        <p className="text-gray-600">No tasks found for this lead on {selectedDate}</p>
                    </div>
                )}

                {/* Status Confirmation Modal */}
                {statusModal.show && (
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Complete Task</h3>
                                <p className="text-gray-600 mb-6">Has the confirmation email been received for this job application?</p>

                                <div className="flex gap-4 mb-8">
                                    <button
                                        onClick={() => setEmailReceived('yes')}
                                        className={`flex-1 py-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${emailReceived === 'yes'
                                            ? 'border-green-500 bg-green-50 text-green-700'
                                            : 'border-gray-200 hover:border-green-200 hover:bg-gray-50 text-gray-600'
                                            }`}
                                    >
                                        <span className="text-2xl">✅</span>
                                        <span className="font-bold">Yes</span>
                                    </button>
                                    <button
                                        onClick={() => setEmailReceived('no')}
                                        className={`flex-1 py-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${emailReceived === 'no'
                                            ? 'border-red-500 bg-red-50 text-red-700'
                                            : 'border-gray-200 hover:border-red-200 hover:bg-gray-50 text-gray-600'
                                            }`}
                                    >
                                        <span className="text-2xl">❌</span>
                                        <span className="font-bold">No</span>
                                    </button>
                                </div>

                                {emailReceived === 'no' && (
                                    <div className="mb-6 animate-slide-down">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Upload Proof Screenshot (Mandatory)
                                        </label>
                                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors cursor-pointer relative">
                                            <div className="space-y-1 text-center">
                                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <div className="flex text-sm text-gray-600">
                                                    <span className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
                                                        {screenshot ? screenshot.name : 'Click to upload screenshot'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500">PNG, JPG, JPEG, WEBP, PDF up to 5MB</p>
                                            </div>
                                            <input
                                                type="file"
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                accept=".jpg,.jpeg,.png,.webp,.pdf"
                                                onChange={handleScreenshotFileChange}
                                            />
                                        </div>
                                        {renderScreenshotPreview()}
                                    </div>
                                )}

                                {emailReceived === 'yes' && (
                                    <div className="mb-6 animate-slide-down">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Please enter subject of the confirmation email (Mandatory)
                                        </label>
                                        <input
                                            type="text"
                                            value={emailSubject}
                                            onChange={(e) => setEmailSubject(e.target.value)}
                                            placeholder="Enter confirmation email subject"
                                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-gray-800 shadow-sm"
                                        />
                                        <div className="flex justify-between items-center mt-1.5 px-0.5">
                                            <span className="text-xs text-gray-400">Must be more than 10 characters</span>
                                            <span className={`text-xs font-semibold ${emailSubject.trim().length > 10 ? 'text-green-600' : 'text-amber-500'}`}>
                                                {emailSubject.trim().length} / 11+ chars
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={submitStatusModal}
                                        disabled={
                                            uploading ||
                                            emailReceived === null ||
                                            (emailReceived === 'no' && !screenshot) ||
                                            (emailReceived === 'yes' && (!emailSubject || emailSubject.trim().length <= 10))
                                        }
                                        className={`w-full py-3 rounded-lg font-bold text-white shadow-lg transition-all ${uploading ||
                                                emailReceived === null ||
                                                (emailReceived === 'no' && !screenshot) ||
                                                (emailReceived === 'yes' && (!emailSubject || emailSubject.trim().length <= 10))
                                                ? 'bg-gray-300 cursor-not-allowed'
                                                : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
                                            }`}
                                    >
                                        {uploading ? 'Processing...' : 'Confirm Status Update'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setStatusModal({ show: false, taskId: null, newStatus: '' });
                                            setEmailReceived(null);
                                            setEmailSubject('');
                                        }}
                                        disabled={uploading}
                                        className="w-full py-2 text-gray-500 hover:text-gray-700 font-medium transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Simple Confirmation Modal */}
                {confirmModal.show && (
                    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-bounce-in">
                            <div className="p-8 text-center">
                                <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl shadow-inner">
                                    ?
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Confirm Completion</h3>
                                <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                                    Are you sure you want to mark this task as <span className="font-bold text-blue-600">Completed</span>?
                                    This action will lock the task.
                                </p>

                                <div className="mb-6 text-left">
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                                        Please enter subject of the confirmation email (Mandatory)
                                    </label>
                                    <input
                                        type="text"
                                        value={emailSubject}
                                        onChange={(e) => setEmailSubject(e.target.value)}
                                        placeholder="Enter confirmation email subject"
                                        className="w-full px-3.5 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs text-gray-800 shadow-sm"
                                    />
                                    <div className="flex justify-between items-center mt-1 px-0.5">
                                        <span className="text-[10px] text-gray-400">Must be more than 10 characters</span>
                                        <span className={`text-[10px] font-semibold ${emailSubject.trim().length > 10 ? 'text-green-600' : 'text-amber-500'}`}>
                                            {emailSubject.trim().length} / 11+ chars
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        onClick={() => {
                                            setConfirmModal({ show: false, taskId: null });
                                            setEmailSubject('');
                                        }}
                                        className="py-3 px-4 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all active:scale-95"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={submitConfirmModal}
                                        disabled={!emailSubject || emailSubject.trim().length <= 10}
                                        className={`py-3 px-4 rounded-xl font-bold text-white transition-all active:scale-95 ${(!emailSubject || emailSubject.trim().length <= 10)
                                                ? 'bg-gray-300 cursor-not-allowed shadow-none'
                                                : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200'
                                            }`}
                                    >
                                        Yes, Do it
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Not Relevant Modal */}
                {notRelevantModal.show && (
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Not Relevant</h3>
                                <p className="text-gray-600 mb-4">Why is this job not relevant for the lead?</p>

                                <div className="space-y-3 mb-6">
                                    {[
                                        "Job role is not relevant for the client",
                                        "Description doesn't match with client experience",
                                        "Skill set mismatch",
                                        "Does not match with lead specifications",
                                        "Client doesn't want to apply for this job role",
                                        "Other"
                                    ].map((option) => (
                                        <label key={option} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                            <input
                                                type="radio"
                                                name="notRelevantReason"
                                                value={option}
                                                checked={notRelevantModal.reason === option}
                                                onChange={(e) => setNotRelevantModal(prev => ({ ...prev, reason: e.target.value }))}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-3 text-sm text-gray-700">{option}</span>
                                        </label>
                                    ))}

                                    {notRelevantModal.reason === 'Other' && (
                                        <textarea
                                            value={notRelevantModal.customReason}
                                            onChange={(e) => setNotRelevantModal(prev => ({ ...prev, customReason: e.target.value }))}
                                            placeholder="Please specify the reason..."
                                            className="w-full mt-2 p-3 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                            rows="3"
                                        />
                                    )}
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setNotRelevantModal({ show: false, taskId: null, reason: '', customReason: '' });
                                            setScreenshot(null);
                                        }}
                                        disabled={uploading}
                                        className="flex-1 py-2 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={submitNotRelevantModal}
                                        disabled={uploading || !notRelevantModal.reason || (notRelevantModal.reason === 'Other' && !notRelevantModal.customReason.trim())}
                                        className={`flex-1 py-2 rounded-lg font-bold text-white transition-all ${(uploading || !notRelevantModal.reason || (notRelevantModal.reason === 'Other' && !notRelevantModal.customReason.trim()))
                                            ? 'bg-gray-300 cursor-not-allowed shadow-none'
                                            : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200'
                                            }`}
                                    >
                                        {uploading ? 'Updating...' : 'Update'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}


                {/* Generic Status Confirmation Modal */}
                {genericConfirmModal.show && (
                    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
                            <div className="p-8 text-center">
                                <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-5 text-3xl shadow-inner">
                                    ⚠️
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Confirm Status Change</h3>
                                <p className="text-gray-500 mb-1 text-sm">You are about to change the status to</p>
                                <p className="text-blue-600 font-bold text-lg mb-6">
                                    {genericConfirmModal.newStatus.replace(/_/g, ' ')}
                                </p>

                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => {
                                            setGenericConfirmModal({ show: false, taskId: null, newStatus: '' });
                                            setScreenshot(null);
                                        }}
                                        className="py-2.5 px-4 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all active:scale-95"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={async () => {
                                            const { taskId, newStatus } = genericConfirmModal;
                                            setGenericConfirmModal({ show: false, taskId: null, newStatus: '' });
                                            await updateTaskStatus(taskId, newStatus, undefined, screenshot);
                                            setScreenshot(null);
                                        }}
                                        disabled={uploading}
                                        className={`py-2.5 px-4 rounded-xl font-bold text-white transition-all active:scale-95 flex items-center justify-center ${uploading
                                                ? 'bg-gray-300 cursor-not-allowed shadow-none'
                                                : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200'
                                            }`}
                                    >
                                        {uploading ? 'Processing...' : 'Yes, Update'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Toast Notification */}
                {toast.show && (
                    <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
                        <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${toast.type === 'success'
                            ? 'bg-green-500 text-white'
                            : 'bg-red-500 text-white'
                            }`}>
                            {toast.type === 'success' ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                            <span className="font-medium">{toast.message}</span>
                        </div>
                    </div>
                )}
            </div>
            <ConfirmModal
                isOpen={!!deleteTaskTarget}
                title="Delete Task"
                message={`Are you sure you want to delete this task for "${deleteTaskTarget ? deleteTaskTarget.jobTitle || deleteTaskTarget.title || 'Job' : ''}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                loading={isDeletingTask}
                onConfirm={() => deleteTask(deleteTaskTarget)}
                onCancel={() => setDeleteTaskTarget(null)}
                danger={true}
            />
        </div >
    );
}

export default LeadTasksView;
